export interface CorrectedReportReaderRow {
  store_id?: string | null;
  store_name: string;
  current_period_end: string | null;
  corrected_current_sessions: number | null;
  corrected_previous_sessions: number | null;
  session_metric_available: boolean | null;
  previous_session_metric_available: boolean | null;
  session_missing_day_count?: number | null;
  previous_session_missing_day_count?: number | null;
  corrected_expected_store_count?: number | null;
  corrected_run_id?: string | null;
  current_checkout_successes: number;
  prev_checkout_successes: number;
  store?: string | null;
  account?: string | null;
}

export function hasCompleteFrozenRosterCoverage(
  reports: CorrectedReportReaderRow[],
): boolean {
  const expectedCounts = new Set(
    reports
      .map((report) => report.corrected_expected_store_count)
      .filter((value): value is number => typeof value === 'number' && Number.isInteger(value) && value > 0),
  );
  if (expectedCounts.size !== 1) return false;
  const runIds = new Set(
    reports
      .map((report) => report.corrected_run_id?.trim())
      .filter((value): value is string => Boolean(value)),
  );
  if (runIds.size !== 1 || reports.some((report) => !report.corrected_run_id?.trim())) return false;
  const expectedCount = [...expectedCounts][0];
  const actualIds = reports.map((report) => report.store_id?.trim()).filter(Boolean) as string[];
  const actual = new Set(actualIds);
  return reports.length === expectedCount
    && actualIds.length === reports.length
    && actual.size === expectedCount;
}

export interface CorrectedGlobalReportingSummary {
  periodStart: string;
  periodEnd: string | null;
  totalSessions: number | null;
  sessionChangePct: number | null;
  sessionCoverage: { available: number; total: number };
  totalCheckouts: number;
  checkoutChangePct: number | null;
}

export interface CorrectedStoreMetricRanking {
  storeName: string;
  value: number;
  changePct: number | null;
}

export interface CorrectedReportingRankings {
  sessions: { top: CorrectedStoreMetricRanking[]; bottom: CorrectedStoreMetricRanking[] };
  checkouts: { top: CorrectedStoreMetricRanking[]; bottom: CorrectedStoreMetricRanking[] };
}

const EXCLUDED_REPORTING_STORES = new Set([
  'demo store',
  'error message display',
  'all_stores',
  'trade show stations',
  'quick checkout / printer',
]);

const calculatePercentChange = (current: number, previous: number): number | null => {
  if (!Number.isFinite(previous) || previous === 0) return null;
  if (!Number.isFinite(current)) return null;
  return ((current - previous) / previous) * 100;
};

function getStoreLabel(report: CorrectedReportReaderRow): string {
  return report.store_name || report.store || report.account || 'Unnamed Store';
}

function isIncludedStore(report: CorrectedReportReaderRow): boolean {
  return !EXCLUDED_REPORTING_STORES.has(getStoreLabel(report).trim().toLowerCase());
}

function availableCurrentSession(report: CorrectedReportReaderRow): number | null {
  return report.session_metric_available === true
    && (report.session_missing_day_count == null || report.session_missing_day_count === 0)
    && typeof report.corrected_current_sessions === 'number'
    && Number.isFinite(report.corrected_current_sessions)
    ? report.corrected_current_sessions
    : null;
}

function availablePreviousSession(report: CorrectedReportReaderRow): number | null {
  return report.previous_session_metric_available === true
    && (report.previous_session_missing_day_count == null || report.previous_session_missing_day_count === 0)
    && typeof report.corrected_previous_sessions === 'number'
    && Number.isFinite(report.corrected_previous_sessions)
    ? report.corrected_previous_sessions
    : null;
}

export function buildGlobalReportingSummary(
  reports: CorrectedReportReaderRow[],
  periodStart: string,
): CorrectedGlobalReportingSummary | null {
  const included = reports.filter(isIncludedStore);
  if (!included.length) return null;

  const currentValues = included
    .map(availableCurrentSession)
    .filter((value): value is number => value !== null);
  const comparisonRows = included
    .map((report) => ({
      current: availableCurrentSession(report),
      previous: availablePreviousSession(report),
    }))
    .filter((row): row is { current: number; previous: number } => row.current !== null && row.previous !== null);
  const totalSessions = currentValues.length
    ? currentValues.reduce((sum, value) => sum + value, 0)
    : null;
  const comparisonCurrent = comparisonRows.reduce((sum, row) => sum + row.current, 0);
  const comparisonPrevious = comparisonRows.reduce((sum, row) => sum + row.previous, 0);
  const totalCheckouts = included.reduce((sum, report) => sum + (report.current_checkout_successes || 0), 0);
  const previousCheckouts = included.reduce((sum, report) => sum + (report.prev_checkout_successes || 0), 0);

  return {
    periodStart,
    periodEnd: included[0]?.current_period_end ?? null,
    totalSessions,
    sessionCoverage: { available: currentValues.length, total: included.length },
    // A change must describe the same cohort as the displayed current total.
    sessionChangePct: comparisonRows.length === currentValues.length && comparisonRows.length
      ? calculatePercentChange(comparisonCurrent, comparisonPrevious)
      : null,
    totalCheckouts,
    checkoutChangePct: calculatePercentChange(totalCheckouts, previousCheckouts),
  };
}

export function buildStoreRankings(reports: CorrectedReportReaderRow[]): CorrectedReportingRankings {
  const included = reports.filter(isIncludedStore);
  const rank = (items: CorrectedStoreMetricRanking[]) => ({
    top: [...items].sort((a, b) => b.value - a.value).slice(0, 5),
    bottom: [...items].sort((a, b) => a.value - b.value).slice(0, 5),
  });
  const sessions = included.flatMap((report) => {
    const current = availableCurrentSession(report);
    if (current === null) return [];
    const previous = availablePreviousSession(report);
    return [{
      storeName: getStoreLabel(report),
      value: current,
      changePct: previous === null ? null : calculatePercentChange(current, previous),
    }];
  });
  const checkouts = included.map((report) => ({
    storeName: getStoreLabel(report),
    value: report.current_checkout_successes || 0,
    changePct: calculatePercentChange(
      report.current_checkout_successes || 0,
      report.prev_checkout_successes || 0,
    ),
  }));
  return { sessions: rank(sessions), checkouts: rank(checkouts) };
}
